import React, { forwardRef } from "react";
import styled, { css } from "styled-components";

import { hexToCSSFilter } from "hex-to-css-filter";
import iconLoading from "images/ic_loading.svg";
import iconLock from "images/ic_lock_green.png";
import theme from "styles/theme";
import tinycolor from "tinycolor2";

const StyledButton = styled.button`
  ${(props) =>
    props.$isFullWidth || props.$defaultWidth ? "width: 100%" : ""};
  ${(props) =>
    props.$defaultWidth ? `max-width: ${props.$defaultWidth}px` : ""};

  display: inline-flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  border-radius: 120px;
  white-space: nowrap;
  border: none;
  cursor: ${(props) =>
    props.$isDisabled ||
    props.$isInactive ||
    props.$isSelected ||
    props.$isLoading
      ? "default"
      : "pointer"};
  pointer-events: ${(props) =>
    props.$isDisabled ||
    props.$isInactive ||
    props.$isSelected ||
    props.$isLoading
      ? "none"
      : "auto"};
  opacity: ${(props) => {
    let opacity = 1;
    if (props.$isDisabled) {
      opacity = 0.25;
    }
    return opacity;
  }};

  text-decoration: none;
  user-select: none;

  ${(props) => {
    let iconMarginRight = 8;
    let iconSize = 24;
    let height = 40;

    let baseStyle = `
      padding: 0 ${theme.spacing.unit * 2}px;
      font-family: 'Inter';
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 150%;
    `;
    let sizeStyle = "";
    let iconStyle = "";
    let iconHoverStyle = "";

    if (props.$isSmall) {
      iconMarginRight = 4;
      iconSize = 16;
      height = 32;

      sizeStyle = `
        font-size: 14px;
        line-height: 100%;
        ${props.$isNormalWeight ? "font-weight: normal;" : ""}
        padding: 0 12px;
      `;
    } else if (props.$isLarge) {
      height = 48;

      sizeStyle = `
        line-height: 100%;
      `;
    } else if (props.$isExtraLarge) {
      height = 56;
      iconMarginRight = 16;

      sizeStyle = `
        font-size: 20px;
        line-height: 100%;
      `;
    }

    sizeStyle += `
      height: ${height}px;
    `;

    let iconOnlyStyle = "";
    if (props.$isIconOnly) {
      iconSize = 24;
      iconMarginRight = 0;
      iconOnlyStyle += `
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
        border-radius: 50%;
        width: ${height}px;
      `;
    }

    if (props.$icon) {
      iconStyle = `
        &::before {
          content: "";
          display: block;
          background: url(${props.$icon}) no-repeat;
          background-size: contain;
          width: ${iconSize}px;
          height: ${iconSize}px;
          float: left;
          margin: 0 ${iconMarginRight}px 0 0;
          ${props.$isLoading ? "visibility: hidden;" : ""}
          ${props.$isIconHoverOnly ? "visibility: hidden;" : ""}
          ${props.$hasIconTransition ? "transition: all 0.3s ease;" : ""}
        }
      `;
    }
    if (props.$iconHover) {
      iconHoverStyle = `
        &:hover {
          &::before {
            background: url(${props.$iconHover}) no-repeat;
          }
        }
      `;
    } else if (props.$isIconHoverOnly) {
      iconHoverStyle = `
        &:hover {
          &::before {
            visibility: visible;
          }
        }
      `;
    }
    return baseStyle + sizeStyle + iconStyle + iconHoverStyle + iconOnlyStyle;
  }};

  ${theme.media.phone`
    ${(props) => (props.$shouldExpandOnMobile ? "width: 100%" : "")};
  `};

  ${(props) => {
    if (props.$isLoading) {
      return LoadingButton;
    } else if (props.$isSecondary) {
      return SecondaryButton;
    } else if (props.$isGhost) {
      return GhostButton;
    } else if (props.$isBrandedPrimary) {
      return BrandedPrimaryButton;
    } else if (props.$isBrandedSecondary) {
      return BrandedSecondaryButton;
    } else if (props.$isBrandedGhost) {
      return BrandedGhostButton;
    } else if (props.$isDashboard) {
      return DashboardButton;
    } else {
      return PrimaryButton;
    }
  }};
`;

// Hover: background changes
// Focus: background changes, text and icon are more transparent
// Active: whole button is more transparent
const PrimaryButton = css`
  color: ${theme.colors.white};
  background-color: ${theme.colors.gray4};

  &::before {
    filter: brightness(0) saturate(100%)
      ${hexToCSSFilter(theme.colors.white).filter};
  }

  ${theme.media.hoverSupported`
    &:hover {
      background-color: ${theme.colors.defaultButtonHover};
    }
  `}

  &:focus-visible {
    background-color: ${theme.colors.defaultButtonHover};
    color: ${tinycolor(theme.colors.white).setAlpha(0.75).toRgbString()};

    &::before {
      opacity: 0.75;
    }
  }

  &:active {
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

// Hover: no change
// Focus: border changes, text and icon are more transparent
// Active: whole button is more transparent
const SecondaryButton = css`
  color: ${theme.colors.black};
  background-color: transparent;
  box-shadow: inset 0px 0px 0px 2px ${theme.colors.gray1};

  &::before {
    filter: brightness(0) saturate(100%)
      ${(props) =>
        hexToCSSFilter(props.$iconColor ? props.$iconColor : theme.colors.black)
          .filter};
  }

  &:focus-visible {
    box-shadow: inset 0px 0px 0px 2px ${theme.colors.gray1};
    color: ${tinycolor(theme.colors.black).setAlpha(0.75).toRgbString()};

    &::before {
      opacity: 0.75;
    }
  }

  &:active {
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

// Hover: background changes
// Focus: no change?
// Active: background changes, whole button is more transparent
const GhostButton = css`
  color: ${theme.colors.black};
  background-color: transparent;

  &::before {
    filter: brightness(0) saturate(100%)
      ${hexToCSSFilter(theme.colors.black).filter};
  }

  ${theme.media.hoverSupported`
    &:hover {
      background-color: ${tinycolor(theme.colors.defaultButtonHover)
        .setAlpha(0.15)
        .toRgbString()};
    }
  `}

  &:active {
    background-color: ${tinycolor(theme.colors.defaultWidth)
      .setAlpha(0.15)
      .toRgbString()};
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

// Hover: background changes
// Focus: background changes, text and icon are more transparent
// Active: whole button is more transparent
const BrandedPrimaryButton = css`
  color: ${(props) =>
    props.$brandingData?.buttonTextColor || props.$color || theme.colors.white};
  background-color: ${(props) =>
    props.$brandingData?.buttonColor ||
    props.$backgroundColor ||
    theme.colors.gray4};

  &::before {
    ${(props) => `filter: brightness(0) saturate(100%)
      ${
        hexToCSSFilter(
          props.$brandingData?.buttonTextColor ||
            props.$color ||
            theme.colors.white
        ).filter
      }`};
  }

  ${theme.media.hoverSupported`
    &:hover {
      background-color: ${(props) =>
        props.$brandingData?.buttonHoverColor ||
        props.$hoverBackgroundColor ||
        theme.colors.defaultButtonHover};
    }
  `}

  &:focus-visible {
    background-color: ${(props) =>
      props.$brandingData?.buttonHoverColor ||
      props.$hoverBackgroundColor ||
      theme.colors.defaultButtonHover};
    color: ${(props) =>
      tinycolor(
        props.$brandingData?.buttonTextColor ||
          props.$color ||
          theme.colors.white
      )
        .setAlpha(0.75)
        .toRgbString()};

    &::before {
      opacity: 0.75;
    }
  }

  &:active {
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

// Hover: border changes
// Focus: border changes, text and icon are more transparent
// Active: whole button is more transparent
const BrandedSecondaryButton = css`
  color: ${(props) =>
    props.$brandingData?.primaryTextColor ||
    props.$color ||
    theme.colors.black};
  background-color: transparent;
  box-shadow: inset 0px 0px 0px 2px
    ${(props) =>
      props.$brandingData?.primaryTextColor ||
      props.$borderColor ||
      theme.colors.black};

  &::before {
    ${(props) => `filter: brightness(0) saturate(100%)
      ${
        hexToCSSFilter(
          props.$brandingData?.primaryTextColor ||
            props.$color ||
            theme.colors.black
        ).filter
      }`};
  }

  ${theme.media.hoverSupported`
    &:hover {
      box-shadow: inset 0px 0px 0px 2px
          ${(props) =>
            props.$brandingData?.primaryTextColor ||
            props.$borderColor ||
            theme.colors.black};
    }
  `}

  &:focus-visible {
    box-shadow: inset 0px 0px 0px 2px
      ${(props) =>
        props.$brandingData?.primaryTextColor ||
        props.$borderColor ||
        theme.colors.black};
    color: ${(props) =>
      tinycolor(
        props.$brandingData?.primaryTextColor ||
          props.$color ||
          theme.colors.black
      )
        .setAlpha(0.75)
        .toRgbString()};

    &::before {
      opacity: 0.75;
    }
  }

  &:active {
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

// Hover: background changes
// Focus: no change?
// Active: background changes, whole button is more transparent
const BrandedGhostButton = css`
  color: ${(props) =>
    props.$brandingData?.primaryTextColor ||
    props.$color ||
    theme.colors.black};
  background-color: transparent;

  &::before {
    ${(props) => `filter: brightness(0) saturate(100%)
      ${
        hexToCSSFilter(
          props.$brandingData?.primaryTextColor ||
            props.$color ||
            theme.colors.black
        ).filter
      }`};
  }

  ${theme.media.hoverSupported`
    &:hover {
      background-color: ${(props) =>
        tinycolor(
          props.$hoverBackgroundColor || theme.colors.defaultButtonHover
        )
          .setAlpha(0.15)
          .toRgbString()};
    }
  `}

  &:active {
    background-color: ${(props) =>
      props.$hoverBackgroundColor || theme.colors.defaultButtonHover};
    mix-blend-mode: darken;
    opacity: 0.75;
  }
`;

const DashboardButton = css`
  color: ${(props) =>
    props.$isSelected ? theme.colors.black : theme.colors.gray3};
  font-weight: ${(props) => (props.$isSelected ? "bold" : "normal")};
  background-color: transparent;

  &:hover,
  &:focus-visible,
  &:active {
    color: ${theme.colors.black};
    background-color: ${theme.colors.gray1};
  }

  ${(props) => {
    if (props.$icon) {
      return `
        &::before {
          filter: brightness(0) saturate(100%) ${
            props.$isSelected
              ? hexToCSSFilter(theme.colors.black).filter
              : hexToCSSFilter(theme.colors.gray3).filter
          };
        }

        &:hover, &:focus-visible, &:active {
          &::before {
            filter: brightness(0) saturate(100%) ${
              hexToCSSFilter(theme.colors.black).filter
            };
          }
        }
      `;
    }
  }}

  ${(props) => {
    if (props.$isLocked) {
      return `&::after {
        content: "";
        display: block;
        background: url(${iconLock}) no-repeat;
        background-size: contain;
        width: 16px;
        height: 16px;
        float: right;
        margin: 0 0 0 16px;
      }
    `;
    }
  }}
`;

// TODO: Extend this to other button sizes besides medium/default
const LoadingButton = css`
  position: relative;

  color: transparent;
  background-color: ${theme.colors.gray4};
  mix-blend-mode: darken;
  opacity: 0.75;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -8px;
    margin-top: -8px;

    background: url(${iconLoading}) no-repeat;
    background-size: contain;
    width: 16px;
    height: 16px;
    content: "";
  }
`;

const BetaButton = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  box-shadow: inset 0px 0px 0px 2px ${theme.colors.yellow};
  border-radius: 120px;
  height: 32px;
  box-sizing: border-box;
  margin-left: ${theme.spacing.unit * 1}px;
  background-color: transparent;
  padding: 0 12px;

  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 112%;
  color: ${theme.colors.yellow};
`;

const PillButton = forwardRef((props, ref) => {
  const buttonProps = { ...props };

  // Hide inactive buttons from screen readers
  if (buttonProps.isInactive) {
    buttonProps.tabIndex = "-1";
  }

  const {
    children,
    isBeta,
    isFullWidth,
    defaultWidth,
    isDisabled,
    isInactive,
    isSelected,
    isLoading,
    isExtraSmall,
    isSmall,
    isLarge,
    isExtraLarge,
    icon,
    iconColor,
    isIconOnly,
    iconHover,
    isIconHoverOnly,
    isPrimary,
    isBrandedPrimary,
    isSecondary,
    isBrandedSecondary,
    isGhost,
    isBrandedGhost,
    isDashboard,
    color,
    backgroundColor,
    hoverBackgroundColor,
    borderColor,
    isNormalWeight,
    shouldExpandOnMobile,
    isLocked,
    brandingData,
    hasIconTransition,
    ...rest
  } = buttonProps;

  return (
    <StyledButton
      ref={ref}
      $isFullWidth={isFullWidth}
      $defaultWidth={defaultWidth}
      $isDisabled={isDisabled}
      $isInactive={isInactive}
      $isSelected={isSelected}
      $isLoading={isLoading}
      $isExtraSmall={isExtraSmall}
      $isSmall={isSmall}
      $isLarge={isLarge}
      $isExtraLarge={isExtraLarge}
      $icon={icon}
      $iconColor={iconColor}
      $isIconOnly={isIconOnly}
      $iconHover={iconHover}
      $isIconHoverOnly={isIconHoverOnly}
      $isPrimary={isPrimary}
      $isBrandedPrimary={isBrandedPrimary}
      $isSecondary={isSecondary}
      $isBrandedSecondary={isBrandedSecondary}
      $isGhost={isGhost}
      $isBrandedGhost={isBrandedGhost}
      $isDashboard={isDashboard}
      $color={color}
      $backgroundColor={backgroundColor}
      $hoverBackgroundColor={hoverBackgroundColor}
      $borderColor={borderColor}
      $isNormalWeight={isNormalWeight}
      $shouldExpandOnMobile={shouldExpandOnMobile}
      $isLocked={isLocked}
      $brandingData={brandingData}
      $hasIconTransition={hasIconTransition}
      {...rest}
    >
      {children}
      {isBeta && <BetaButton>BETA</BetaButton>}
    </StyledButton>
  );
});

export default PillButton;
